import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Download, Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import JDForm from '../components/JDForm';
import api from '../api';

export default function JDUpload() {
  const navigate = useNavigate();
  const [jds, setJds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    status: 'Active',
    uploader_id: '',
    assignee_id: ''
  });
  
  useEffect(() => {
    fetchJDs();
  }, [filters]);
  
  const fetchJDs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status_filter', filters.status);
      if (filters.uploader_id) params.append('uploader_id', filters.uploader_id);
      if (filters.assignee_id) params.append('assignee_id', filters.assignee_id);
      
      const response = await api.get(`/jds/list?${params.toString()}`);
      setJds(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch JDs');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (jdId) => {
    if (!confirm('Are you sure you want to delete this JD?')) return;
    
    try {
      await api.delete(`/jds/${jdId}`);
      fetchJDs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete JD');
    }
  };
  
  const handleDownload = async (jdId, clientName, roleTitle) => {
    try {
      const response = await api.get(`/jds/download/${jdId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `JD_${clientName}_${roleTitle}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to download JD');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN');
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gold">Job Descriptions</h1>
          <p className="text-textLight/70 mt-1">Manage and assign job requirements</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-teal hover:bg-teal-light text-white rounded-lg transition-colors shadow-lg"
        >
          <Plus size={20} />
          <span>Upload JD</span>
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-navy-dark border border-teal/30 rounded-xl p-6 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-textLight mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={fetchJDs}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-teal hover:bg-teal-light text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* JD List */}
      <div className="bg-navy-dark border border-teal/30 rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-teal" size={32} />
            <span className="ml-3 text-textLight">Loading JDs...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="text-red-400" size={32} />
            <span className="ml-3 text-red-400">{error}</span>
          </div>
        ) : jds.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FileText className="text-teal/50" size={64} />
            <p className="text-textLight/70 mt-4 text-lg">No job descriptions found</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-teal hover:text-teal-light underline"
            >
              Upload your first JD
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1e3a5f] border-b border-teal/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">JD ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Experience</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Deadline</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Openings</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Assignee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-teal/10">
                {jds.map((jd, index) => (
                  <tr key={jd.id} onClick={() => navigate(`/jd/${jd.id}`)} className={`cursor-pointer hover:bg-teal/5 transition-colors ${
                    index % 2 === 0 ? 'bg-navy/30' : 'bg-navy-dark'
                  }`}>
                    <td className="px-4 py-4 whitespace-nowrap"><button onClick={() => navigate(`/jd/${jd.id}`)} className="font-semibold text-teal hover:underline">JD-{String(jd.id).padStart(4, '0')}</button></td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button onClick={() => navigate(`/jd/${jd.id}`)} className="text-textLight font-semibold hover:text-teal hover:underline">{jd.client_name}</button>
                    </td>
                    <td className="px-4 py-4">
                      <button onClick={() => navigate(`/jd/${jd.id}`)} className="text-left text-textLight hover:text-teal hover:underline">{jd.role_title}</button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-textLight/70">{jd.experience_range || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-textLight/70">{jd.location || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        jd.requirement_type === 'Ongoing'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {jd.requirement_type}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-textLight/70">{formatDate(jd.deadline)}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-textLight">{jd.openings}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-textLight/70">{jd.assigned_to?.length ? jd.assigned_to.map((user) => user.name).join(', ') : jd.assignee?.name || 'Unassigned'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        jd.status === 'Active'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {jd.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        {jd.file_path && (
                          <button
                            onClick={(event) => { event.stopPropagation(); handleDownload(jd.id, jd.client_name, jd.role_title); }}
                            className="p-2 hover:bg-teal/20 rounded-lg transition-colors text-teal"
                            title="Download JD"
                          >
                            <Download size={18} />
                          </button>
                        )}
                        <button
                          onClick={(event) => { event.stopPropagation(); handleDelete(jd.id); }}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                          title="Delete JD"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* JD Form Modal */}
      {showForm && (
        <JDForm
          onClose={() => setShowForm(false)}
          onSuccess={fetchJDs}
        />
      )}
    </div>
  );
}
